// The standard roles, as data (ADR 0031, docs/plans/role-permission-upgrades.md). The demo
// seed and every new organisation build their roles from these, and each role remembers its
// template's key. When a slice adds a permission to a template, existing organisations'
// roles are offered it in Settings > Role updates; nothing is granted without an
// administrator accepting it.
//
// To give a standard role a new permission: add it here (and to PERMISSIONS, with its
// migration, ADR 0011). The Administrator template is every permission; a test checks that.

import { PERMISSION_LIST, type Permission } from './permissions.ts';

export interface RoleTemplate {
  /** Stable: stored on each role made from the template. Never rename one. */
  key: string;
  name: string;
  description: string;
  requires2fa: boolean;
  permissions: readonly Permission[];
}

/** The mortuary attendant's work (docs/plans/phase2-mortuary-and-notifications.md §3.1). */
// No `deaths.read`: causes of death can reveal sensitive conditions (plan D10). The board
// and the case show the place and time of death the attendant needs.
const MORTUARY_ATTENDANT_PERMISSIONS: readonly Permission[] = [
  'patients.read',
  'mortuary.read',
  'mortuary.intake',
  'mortuary.release.request',
];

/** What nurses and clinicians do at triage (docs/plans/phase1-triage.md §3.1). */
const NURSE_PERMISSIONS: readonly Permission[] = [
  'patients.read',
  'visits.read',
  'queues.read',
  // Appointments (docs/plans/phase2-appointments.md §3.1).
  'appointments.read',
  'queues.manage',
  'clinical.read',
  'triage.record',
  'allergies.record',
  // Isolation and precaution flags (docs/plans/phase3-operations-modules.md §5).
  'infection.flag',
];

/** Clinicians request lab tests and see released results (lab plan §3.1). */
const CLINICIAN_LAB_PERMISSIONS: readonly Permission[] = [
  'lab.order',
  'lab.order.cancel',
  'lab.results.read',
  'lab.report.print',
];

const LAB_TECHNOLOGIST_PERMISSIONS: readonly Permission[] = [
  'patients.read',
  'lab.read',
  'lab.collect',
  'lab.result.enter',
  'lab.report.print',
  // Analyzer results that could not be applied by themselves (lab connector plan §3.6).
  'lab.analyzers.review',
  // Quality control: see and record runs (lab QC and microbiology plan §2).
  'lab.qc.read',
  'lab.qc.record',
];

const LAB_MANAGER_PERMISSIONS: readonly Permission[] = [
  ...LAB_TECHNOLOGIST_PERMISSIONS,
  'lab.result.validate',
  'lab.result.authorise',
  'lab.result.correct',
  'lab.results.read',
  'lab.critical.escalations',
  'lab.catalogue.manage',
  'lab.settings.manage',
  'lab.analyzers.manage',
  'lab.order.cancel',
  'catalogue.read',
  // QC set-up and overrides of rejected runs; a second person approves (lab QC plan Q7).
  'lab.qc.manage',
  'lab.qc.override',
];

/**
 * What clinicians do in the consultation (docs/plans/phase1-consultation.md §3.1).
 * TODO(clinical-verify): C18, only clinicians verify allergies; P20, nurses and clinicians
 * both perform procedures.
 */
const CLINICIAN_PERMISSIONS: readonly Permission[] = [
  ...NURSE_PERMISSIONS,
  // Follow-up booking from the consultation (appointments plan §3.1).
  'appointments.book',
  'consultation.record',
  'allergies.verify',
  'procedures.order',
  'procedures.perform',
  'procedures.cancel',
  'imaging.order',
  // Radiology plan R8, TODO(clinical-verify): clinicians read and print imaging reports and
  // may report an exam (a clinical officer reading their own X-ray); they don't verify.
  'imaging.read',
  'imaging.results.read',
  'imaging.report.print',
  'imaging.report.write',
  ...CLINICIAN_LAB_PERMISSIONS,
  'terminology.read',
  'visits.close',
];

/** Maternity and MCH (docs/plans/phase2-maternity-mch.md §3.1). TODO(clinical-verify) M2. */
const MATERNITY_PERMISSIONS: readonly Permission[] = [
  'maternity.read',
  'maternity.record',
  'mch.read',
  'mch.record',
];

/**
 * What every member of staff does, whatever their role (docs/plans/phase3-operations-modules.md
 * §5): report an incident. Added to every template but the Auditor's, who may be from outside.
 */
const EVERY_STAFF_MEMBER: readonly Permission[] = [
  'incidents.report',
  // Roster and attendance (plan §5): see the roster, ask to swap, clock in and out.
  'roster.read',
  'attendance.clock',
];

/** The infection-control nurse (docs/plans/phase3-operations-modules.md §2.3, §5). */
const IPC_NURSE_PERMISSIONS: readonly Permission[] = [
  'patients.read',
  'visits.read',
  'inpatient.read',
  'infection.flag',
  'infection.record',
  'infection.read',
  'infection.manage',
];

/** The maintenance technician (docs/plans/phase3-operations-modules.md §3.2, §5). */
const MAINTENANCE_PERMISSIONS: readonly Permission[] = [
  'assets.read',
  'assets.manage',
  'assets.maintain',
  'stock.read',
];

/** Incident review (docs/plans/phase3-operations-modules.md §1.3, plan I2). */
const SAFETY_LEAD_PERMISSIONS: readonly Permission[] = [
  'patients.read',
  'incidents.report',
  'incidents.read',
  'incidents.manage',
];

const STANDARD_TEMPLATES: readonly RoleTemplate[] = [
  {
    key: 'administrator',
    name: 'Administrator',
    description: 'Manages users, roles and facilities.',
    requires2fa: true,
    permissions: PERMISSION_LIST,
  },
  {
    key: 'auditor',
    name: 'Auditor',
    description: 'Reads the audit log and the user list, and reviews break-glass openings.',
    requires2fa: false,
    // Privacy tooling plan P3, TODO(legal-verify): the auditor (the DPO's role once named)
    // reviews break-glass openings.
    permissions: ['audit.read', 'admin.users.read', 'records.break_glass.review'],
  },
  {
    key: 'reception',
    name: 'Reception',
    description: 'Registers patients and checks them in.',
    requires2fa: false,
    permissions: [
      'patients.read',
      'patients.register',
      'patients.update',
      'visits.read',
      'visits.open',
      'visits.cancel',
      'visits.close',
      // Reception redirects patients between queues (triage plan P4).
      'queues.read',
      'queues.manage',
      // SHA cover at check-in (docs/plans/phase1-sha.md §8.1).
      'sha.eligibility.check',
      'sha.visit.manage',
      // Scanned papers brought in at the desk (consultation plan §3.1, P10).
      'documents.upload',
      // Appointments and SMS reminder answers (appointments plan §3.1).
      'appointments.read',
      'appointments.book',
      // Insurance cards and insurers' approvals (private insurance plan §4.1).
      'catalogue.read',
      'insurance.coverage.manage',
      'insurance.preauth.manage',
      // Casualty plan C10, TODO(clinical-verify): reception registers unknown patients
      // and asks to merge them once identified.
      'casualty.register',
      'patients.merge.request',
      // Referrals plan F1, TODO(clinical-verify): reception logs referrals received,
      // records what receiving facilities say, and finds the referring facility in the
      // national registry.
      'referrals.read',
      'referrals.incoming.record',
      'referrals.track',
      'registries.search',
      // Patient portal plan P12, provisional: reception enrols patients in the portal with
      // their consent and asks for feedback on a visit by SMS.
      'portal.accounts.manage',
      'feedback.request.send',
    ],
  },
  {
    key: 'nurse',
    name: 'Nurse',
    description: 'Runs queues, triages patients and records allergies.',
    requires2fa: false,
    permissions: [
      ...NURSE_PERMISSIONS,
      'procedures.perform',
      'prescriptions.read',
      'documents.read',
      'documents.upload',
      // Inpatient plan B8, TODO(clinical-verify): nurses give beds, move patients and
      // manage bed states.
      'inpatient.read',
      'inpatient.move',
      'inpatient.beds.manage',
      // Ward rounds and nursing plan W7, TODO(clinical-verify): vitals rounds, nursing
      // notes and handover.
      'nursing.record',
      // Kitchen plan §3, TODO(clinical-verify): nurses order diets and see housekeeping.
      'diet.order',
      'housekeeping.read',
      // eMAR plan E7, TODO(clinical-verify): nurses give and record medicine doses.
      'medication.administer',
      // Theatre plan T9, TODO(clinical-verify): nurses record the safety checklist.
      'theatre.read',
      'theatre.checklist.record',
      // Casualty plan C10, TODO(clinical-verify).
      'casualty.register',
      'patients.merge.request',
      // Referrals plan F1, TODO(clinical-verify).
      'referrals.read',
      // Maternity plan M2, TODO(clinical-verify): nurses and midwives record maternity and
      // MCH (there is no Midwife role yet).
      ...MATERNITY_PERMISSIONS,
      // Mortuary plan D10, N7, TODO(clinical-verify): nurses and midwives record births
      // and read death records.
      'deaths.read',
      'births.read',
      'births.record',
    ],
  },
  {
    key: 'clinician',
    name: 'Clinician',
    description:
      'Sees patients in consultation: notes, diagnoses, allergy review, tests, procedures and prescriptions.',
    requires2fa: false,
    // C28: only clinicians prescribe (consultation plan §14, TODO(clinical-verify)).
    permissions: [
      ...CLINICIAN_PERMISSIONS,
      // Privacy tooling plan P1, TODO(clinical-verify): clinicians label a visit they are
      // cleared for; taking a label off is for the clinical lead and records officer.
      'visits.label',
      'prescriptions.write',
      'prescriptions.read',
      'formulary.read',
      // Asking SHA to approve a service (SHA plan §8.1, C2: clinicians only).
      'preauth.read',
      'preauth.request',
      'registries.search',
      // Clinical documents (consultation plan §3.1, C4).
      'sick_sheets.issue',
      'referrals.write',
      // Referrals plan F1, TODO(clinical-verify): clinicians follow their referrals.
      'referrals.read',
      'referrals.track',
      'documents.read',
      'documents.upload',
      // Inpatient plan B8, TODO(clinical-verify): clinicians admit, move and discharge.
      'inpatient.read',
      'inpatient.admit',
      'inpatient.move',
      'inpatient.discharge',
      // Ward rounds and nursing plan W7, TODO(clinical-verify): clinicians take vitals on
      // rounds and read the chart.
      'nursing.record',
      // Kitchen plan §3, TODO(clinical-verify): clinicians order diets.
      'diet.order',
      // Theatre plan T9, TODO(clinical-verify): clinicians book cases, write operation
      // notes and record theatre services used.
      'theatre.read',
      'theatre.book',
      'theatre.note.write',
      'theatre.charges.record',
      // Casualty plan C10, TODO(clinical-verify).
      'casualty.register',
      'patients.merge.request',
      // Maternity plan M2, TODO(clinical-verify).
      ...MATERNITY_PERMISSIONS,
      // Mortuary plan D5, D10, TODO(clinical-verify): clinicians certify and record
      // deaths, ask for a death record entered in error to be corrected, record births.
      'deaths.read',
      'deaths.record',
      'deaths.correct.request',
      'births.read',
      'births.record',
      // Their own revenue share statements (docs/plans/phase3-accounting-and-revenue-share.md §4).
      'revenue_share.own.read',
    ],
  },
  {
    key: 'radiographer',
    name: 'Radiographer',
    description: 'Schedules imaging requests, marks them done and prints imaging reports.',
    requires2fa: false,
    // Radiology plan §3.1 and R8, TODO(clinical-verify).
    permissions: [
      'patients.read',
      'visits.read',
      'clinical.read',
      'imaging.read',
      'imaging.schedule',
      'imaging.perform',
      'imaging.results.read',
      'imaging.report.print',
    ],
  },
  {
    key: 'radiologist',
    name: 'Radiologist',
    description: 'Reports and verifies imaging, and sets up imaging tests in Settings > Imaging.',
    requires2fa: false,
    // Radiology plan §3.1 and R8, TODO(clinical-verify).
    permissions: [
      'patients.read',
      'visits.read',
      'clinical.read',
      'catalogue.read',
      'imaging.read',
      // Radiologists often do ultrasound themselves, and withdraw a reported exam
      // entered in error (plan R14).
      'imaging.perform',
      'imaging.report.write',
      'imaging.report.verify',
      'imaging.results.read',
      'imaging.report.print',
      'imaging.catalogue.manage',
    ],
  },
  {
    key: 'pharmacist_in_charge',
    name: 'Pharmacist in charge',
    description:
      'Keeps the formulary, dispenses, approves pharmacy returns and sets supply classes.',
    requires2fa: false,
    permissions: [
      'patients.read',
      'visits.read',
      'prescriptions.read',
      'formulary.read',
      'formulary.manage',
      'catalogue.read',
      'stock.read',
      'billing.read',
      'pharmacy.read',
      'pharmacy.dispense',
      'pharmacy.substitute',
      'pharmacy.sell',
      'pharmacy.return.request',
      'pharmacy.return.approve',
      'pharmacy.supply_class.manage',
      // Analytics plan A2: the pharmacy and stock dashboard.
      'analytics.read',
      // eMAR plan E7, TODO(clinical-verify): verifies inpatient medication orders.
      'pharmacy.verify',
      // Asks the main store for stock (procurement plan §2.3).
      'stock.requisition',
      'payments.take',
      'cashier.shift.open',
      'cashier.shift.close',
    ],
  },
  {
    key: 'clinical_lead',
    name: 'Clinical lead',
    description: 'Sets and approves clinical rules: vital limits, ranges, triage categories.',
    // Clinical rules change what is refused and flagged at triage (triage plan §3.1).
    requires2fa: true,
    permissions: [
      ...NURSE_PERMISSIONS,
      'clinical_rules.manage',
      'clinical_rules.approve',
      // Incident register (docs/plans/phase3-operations-modules.md §5): reads, does not review.
      'incidents.read',
      // Infection control: the line list and recording infections (plan §5).
      'infection.record',
      'infection.read',
      // The nursing roster and attendance (plan §5).
      'roster.manage',
      'attendance.read',
      'terminology.read',
      'note_templates.manage',
      // Lab ranges are set on each analyte (lab plan §2.6).
      'lab.read',
      'lab.results.read',
      'prescriptions.read',
      'formulary.read',
      // SHA plan C1 (provisional, TODO(clinical-verify)): a senior clinician may go
      // ahead without an approval SHA couldn't give, with a reason.
      'preauth.read',
      'preauth.request',
      'preauth.override',
      // Inpatient plan B8, TODO(clinical-verify): cancelling an admission entered in error.
      'inpatient.read',
      'inpatient.admission.cancel',
      'nursing.record',
      // Kitchen plan §3, TODO(clinical-verify): diet orders, and the diet lists.
      'diet.order',
      'kitchen.manage',
      // eMAR plan E7, TODO(clinical-verify): records doses and sets the dose windows.
      'medication.administer',
      'emar.settings.manage',
      // Radiology plan R8, TODO(clinical-verify).
      'imaging.results.read',
      // Theatre plan T1, T9, TODO(clinical-verify): the clinical lead owns the
      // checklist wording and books cases.
      'theatre.read',
      'theatre.book',
      'theatre.settings.manage',
      // Casualty plan C10, TODO(clinical-verify): the clinical lead approves merging an
      // unknown patient's record into the identified patient's.
      'patients.merge.request',
      'patients.merge.approve',
      // Referrals plan F1, TODO(clinical-verify).
      'referrals.read',
      // Maternity plan M2 and M3, TODO(clinical-verify): the clinical lead sets up the
      // danger-sign lists, partograph lines, vaccines and schedule (all empty until then).
      ...MATERNITY_PERMISSIONS,
      'maternity.settings.manage',
      // Mortuary plan D10, N6, TODO(clinical-verify): the clinical lead approves a death
      // record entered in error and corrects birth notifications.
      'deaths.read',
      'deaths.correct.request',
      'deaths.correct.approve',
      'births.read',
      'births.correct',
      // KHIS plan Q3: the clinical lead reads the quality indicators and sets the
      // facility's own targets (none is set until then).
      'quality.read',
      'quality.settings.manage',
      // Analytics plan A2: flow, lab and inpatient dashboards (lists need the registers).
      'analytics.read',
      // Patient portal plan P12, provisional: the clinical lead handles patient feedback
      // and escalates complaints to incidents.
      'feedback.read',
      'feedback.respond',
      'feedback.escalate',
      // Privacy tooling plan P1 and P7, TODO(clinical-verify): the clinical lead labels and
      // unlabels visits, and reviews correction requests (opening them needs the register).
      'visits.label',
      'visits.unlabel',
      'records.requests.read',
      'records.corrections.review',
    ],
  },
  {
    key: 'mortuary_attendant',
    name: 'Mortuary attendant',
    description:
      'Receives bodies into the mortuary, moves them between slots and records who is collecting.',
    requires2fa: false,
    // Mortuary plan §3.1 and M3, TODO(business).
    permissions: MORTUARY_ATTENDANT_PERMISSIONS,
  },
  {
    key: 'mortuary_in_charge',
    name: 'Mortuary in charge',
    description:
      'Approves releasing bodies, cancels cases entered in error and sets up the mortuary.',
    // Release hands over a body: a second, senior person approves (mortuary plan M3).
    requires2fa: false,
    permissions: [
      ...MORTUARY_ATTENDANT_PERMISSIONS,
      'mortuary.release.approve',
      'mortuary.case.cancel',
      'mortuary.settings.manage',
      'catalogue.read',
    ],
  },
  {
    key: 'lab_technologist',
    name: 'Lab technologist',
    description: 'Collects samples, prints labels and enters lab results.',
    requires2fa: false,
    permissions: LAB_TECHNOLOGIST_PERMISSIONS,
  },
  {
    key: 'lab_manager',
    name: 'Lab manager',
    description:
      'Validates and releases lab results, corrects reports, sets up lab tests and settings.',
    requires2fa: false,
    // Analytics plan A2: the lab turnaround dashboard (aggregates; no registers).
    permissions: [...LAB_MANAGER_PERMISSIONS, 'analytics.read'],
  },
  {
    key: 'finance_administrator',
    name: 'Finance administrator',
    description: 'Manages the service catalogue, prices, payers and schemes.',
    // Prices change revenue (finance plan §3.1, decision 9).
    requires2fa: true,
    permissions: [
      'catalogue.read',
      'catalogue.manage',
      'prices.manage',
      'payers.manage',
      'preauth_list.manage',
      'claims.read',
      'insurance.claims.read',
      // Inpatient packages (docs/plans/phase2-inpatient-billing.md §3.1, TODO(business) IB7).
      'billing.packages.manage',
    ],
  },
  {
    key: 'storekeeper',
    name: 'Storekeeper',
    description: 'Receives, moves and counts stock, requests adjustments, and prepares purchases.',
    requires2fa: false,
    permissions: [
      'stock.read',
      'stock.receive',
      'stock.transfer',
      'stock.count',
      'stock.adjust.request',
      // The asset register: storekeepers see what parts are for (operations plan §5).
      'assets.read',
      // Procurement (docs/plans/phase2-procurement.md §3.1): prepares, never approves.
      'stock.requisition',
      'procurement.read',
      'procurement.requisition',
      'procurement.order',
      'procurement.invoice',
    ],
  },
  {
    key: 'procurement_approver',
    name: 'Procurement approver',
    description: 'Approves purchase requisitions, LPOs, supplier invoices and stock corrections.',
    // Approvals commit the facility's money (CLAUDE.md, domain rule 10), like finance approvers.
    requires2fa: true,
    permissions: [
      'catalogue.read',
      'stock.read',
      'stock.adjust.approve',
      'procurement.read',
      'procurement.approve',
      'procurement.invoice.approve',
    ],
  },
  {
    key: 'cashier',
    name: 'Cashier',
    description: 'Takes payments and runs a cashier shift.',
    requires2fa: false,
    permissions: [
      'patients.read',
      'visits.read',
      'catalogue.read',
      'billing.read',
      'billing.charge.manual',
      'billing.waiver.request',
      'payments.take',
      'payments.refund.request',
      'cashier.shift.open',
      'cashier.shift.close',
      'visits.close',
      'sha.eligibility.check',
      'sha.visit.manage',
      'insurance.coverage.manage',
      'insurance.preauth.manage',
      // Deposits and stay bills use payments.take and billing.read; applying a package
      // to a stay is provisional here (inpatient billing plan, TODO(business) IB7).
      'inpatient.package.apply',
    ],
  },
  {
    key: 'claims_officer',
    name: 'Claims officer',
    description: 'Prepares, sends and follows SHA claims, and fixes rejected ones.',
    requires2fa: false,
    permissions: [
      'patients.read',
      'visits.read',
      'catalogue.read',
      'billing.read',
      'preauth.read',
      'claims.read',
      'claims.submit',
      'claims.write_off.request',
      // Private insurers (private insurance plan §4.1).
      'insurance.preauth.manage',
      'insurance.claims.read',
      'insurance.claims.manage',
      'insurance.remittance.record',
      'insurance.write_off.request',
    ],
  },
  // Medical records and data-subject rights (docs/plans/phase2-records-data-rights.md
  // §3.1). TODO(legal-verify) R11: who logs requests, checks identity and releases
  // records. Releasing whole records is high risk, so the role signs in with two factors.
  {
    key: 'medical_records_officer',
    name: 'Medical records officer',
    description:
      'Logs data-subject requests, checks identity, releases records, reads access logs and tracks charts.',
    requires2fa: true,
    permissions: [
      'patients.read',
      'visits.read',
      'records.requests.read',
      'records.requests.manage',
      'records.export',
      'records.access_log.read',
      // Chart tracking plan C1, TODO(clinical-verify): records officers open, issue and
      // return charts and set the records-room locations and return period.
      'records.charts.read',
      'records.charts.manage',
      'records.settings.manage',
      // Privacy tooling plan P1 and P7, TODO(clinical-verify): records officers label and
      // unlabel visits and review correction requests.
      'visits.label',
      'visits.unlabel',
      'records.corrections.review',
      // Referrals plan F1, TODO(clinical-verify): records officers follow referrals up
      // and download the list.
      'referrals.read',
      'referrals.track',
      'referrals.export',
      // KHIS plan K12 and Q3: the records officer (HRIO) prepares the monthly KHIS
      // figures and reads the quality indicators; someone else approves them.
      'khis.read',
      'khis.submission.prepare',
      'quality.read',
      // Analytics plan A2: the patient flow and inpatient dashboards.
      'analytics.read',
      // Patient portal plan P12, provisional: records officers enrol patients in the portal
      // and handle patient feedback.
      'portal.accounts.manage',
      'feedback.read',
      'feedback.respond',
    ],
  },
  // The pharmacy (dispensing plan §5.1). TODO(clinical-verify): who may hold
  // pharmacy.dispense (plan §18 C4) is the facility's to grant.
  {
    key: 'pharmacist',
    name: 'Pharmacist',
    description: 'Dispenses prescriptions, sells at the counter and asks for returns.',
    requires2fa: false,
    permissions: [
      'patients.read',
      'billing.read',
      'pharmacy.read',
      'pharmacy.dispense',
      'pharmacy.substitute',
      'pharmacy.sell',
      'pharmacy.return.request',
      // eMAR plan E7, TODO(clinical-verify): verifies inpatient medication orders.
      'pharmacy.verify',
      'payments.take',
      'cashier.shift.open',
      'cashier.shift.close',
    ],
  },
  {
    key: 'pharmacy_counter',
    name: 'Pharmacy counter',
    description: 'Sells over-the-counter items and takes payment at the pharmacy counter.',
    requires2fa: false,
    permissions: [
      'pharmacy.read',
      'pharmacy.sell',
      'payments.take',
      'cashier.shift.open',
      'cashier.shift.close',
    ],
  },
  {
    key: 'finance_approver',
    name: 'Finance approver',
    description: 'Decides waivers, discounts, credit notes and refunds; reviews shifts.',
    // Approvers change what patients owe (CLAUDE.md: 2FA for finance approvers).
    requires2fa: true,
    permissions: [
      'patients.read',
      'visits.read',
      'catalogue.read',
      'billing.read',
      'billing.charge.cancel',
      'billing.waiver.approve',
      'billing.credit_note.request',
      'billing.credit_note.approve',
      'billing.settings.manage',
      // The eTIMS signing mode and failed documents (docs/plans/phase3-etims-vscu.md V2, V11).
      'etims.settings.manage',
      'etims.documents.sign_again',
      'payments.refund.approve',
      // Paybill payments that waited for a person (ADR 0020, provisional: see the
      // workflow's open questions on whether cashiers should hold this too).
      'payments.c2b.match',
      'cashier.shift.review',
      // Money reports (reports plan §7.1); no registers, which list patients.
      'reports.summary.read',
      'reports.finance.read',
      'reports.claims.read',
      // Analytics plan A2: the revenue and claims dashboards.
      'analytics.read',
      'claims.read',
      'claims.write_off.approve',
      'insurance.claims.read',
      'insurance.write_off.approve',
      // Revenue share statements are prepared by the accountant and decided here
      // (docs/plans/phase3-accounting-and-revenue-share.md §4, R7).
      'accounting.read',
      'revenue_share.read',
      'revenue_share.approve',
    ],
  },
  {
    // Accounting integration and doctor revenue share
    // (docs/plans/phase3-accounting-and-revenue-share.md §4, A6).
    key: 'accountant',
    name: 'Accountant',
    description:
      'Maps accounts, downloads journals, closes periods and prepares revenue share statements.',
    // Closing a period freezes the books (plan A6).
    requires2fa: true,
    permissions: [
      'accounting.read',
      'accounting.manage',
      'accounting.export',
      'accounting.period.close',
      'revenue_share.read',
      'revenue_share.manage',
      'revenue_share.prepare',
      'revenue_share.payout.record',
      'reports.summary.read',
      'reports.finance.read',
      'reports.claims.read',
      // Analytics plan A2: the revenue and claims dashboards.
      'analytics.read',
      'catalogue.read',
      'procurement.read',
    ],
  },
  {
    // Staff records with licence expiry (docs/plans/phase2-staff-records.md §3, S5).
    key: 'human_resources',
    name: 'Human resources',
    description: 'Keeps staff records, licence details and licence copies.',
    requires2fa: false,
    permissions: [
      'staff.read',
      'staff.manage',
      'practitioners.manage',
      'admin.users.read',
      // Roster and attendance (docs/plans/phase3-operations-modules.md §5).
      'roster.manage',
      'attendance.read',
    ],
  },
  {
    key: 'staff',
    name: 'Staff',
    description: 'No administrative access.',
    requires2fa: false,
    permissions: [],
  },
  {
    // Incident reporting (docs/plans/phase3-operations-modules.md §5).
    key: 'quality_safety_lead',
    name: 'Quality and safety lead',
    description: 'Reviews incidents, records root causes and follows actions to closure.',
    requires2fa: false,
    permissions: SAFETY_LEAD_PERMISSIONS,
  },
  {
    // Infection control (docs/plans/phase3-operations-modules.md §5).
    key: 'infection_control_nurse',
    name: 'Infection prevention and control nurse',
    description: 'Flags patients needing precautions, records infections and keeps the line list.',
    requires2fa: false,
    permissions: IPC_NURSE_PERMISSIONS,
  },
  {
    // Assets and maintenance (docs/plans/phase3-operations-modules.md §5).
    key: 'maintenance_technician',
    name: 'Maintenance technician',
    description:
      'Keeps the asset register, does planned maintenance and repairs, and records parts used.',
    requires2fa: false,
    permissions: MAINTENANCE_PERMISSIONS,
  },
  {
    // Kitchen and dietary (docs/plans/phase3-kitchen-and-housekeeping.md §3): meal lists and
    // tickets only; no patient search, chart or stay.
    key: 'kitchen',
    name: 'Kitchen',
    description: 'Prepares meals from the ward meal lists and prints meal tickets.',
    requires2fa: false,
    permissions: ['kitchen.read'],
  },
  {
    // Housekeeping (docs/plans/phase3-kitchen-and-housekeeping.md §3): beds and areas, never
    // patients.
    key: 'housekeeping',
    name: 'Housekeeping',
    description: 'Cleans beds and areas, records the work and the consumables used.',
    requires2fa: false,
    permissions: ['housekeeping.read', 'housekeeping.record'],
  },
];

const NOT_STAFF_MEMBERS = new Set(['administrator', 'auditor']);

export const ROLE_TEMPLATES: readonly RoleTemplate[] = STANDARD_TEMPLATES.map((t) =>
  NOT_STAFF_MEMBERS.has(t.key)
    ? t
    : { ...t, permissions: [...new Set([...t.permissions, ...EVERY_STAFF_MEMBER])] },
);

const BY_KEY = new Map(ROLE_TEMPLATES.map((t) => [t.key, t]));

/** The template with this key, or undefined for a key this release doesn't know. */
export function roleTemplate(key: string): RoleTemplate | undefined {
  return BY_KEY.get(key);
}
